import React, { useEffect, useState } from 'react'

import cx from 'classnames'
import { flatten } from 'lodash'
import { clearFilters, setDateRange } from 'slices/filters'

import { getDefaultRange } from 'components/DateRangePicker'
import { useAppDispatch } from 'hooks/useRedux'
import { INACTIVE_FILTER } from 'hooks/useSavedFilters'
import {
  FilterConfigAndAvailableValues,
  FilterGroup,
  FilterValues,
  MAX_NUM_FILTER_GROUPS
} from 'utils/filters'
import { formatFilterFromConfig, getFilterConfig, getFirstUnusedFilter } from 'utils/filters'
import {
  addFilter,
  addFilterGroup,
  removeFilter,
  updateFilterOperator,
  updateFilterType,
  updateFilterValue
} from 'utils/filters'
import { fsTrackEvent, FULLSTORY_CUSTOM_EVENT } from 'utils/fullstory'
import { segmentTrack, TrackType } from 'utils/tracking'

import ActionItem from './form/ActionItem'
import FilterGroupedRow from './form/FilterGroupedRow'

import utilityStyles from '../../styles/utility.module.css'
import styles from './styles.module.css'

interface FiltersFormProps {
  filterConfiguration: FilterConfigAndAvailableValues[]
  initialGroups: FilterGroup[]
  dataSources?: any
  onClose?: () => void
  activeFilterId?: number
  calendarOpenDirection?: 'down' | 'up'
  showFormValidation?: boolean
  onChangeFilterGroups: (filterGroups: FilterGroup[]) => void
}

const FiltersForm = ({
  filterConfiguration,
  initialGroups,
  onClose,
  activeFilterId,
  calendarOpenDirection,
  onChangeFilterGroups,
  showFormValidation
}: FiltersFormProps) => {
  const [filterGroups, setFilterGroups] = useState(initialGroups)
  const defaultFilter = formatFilterFromConfig(filterConfiguration[0])
  const firstUnusedFilter = getFirstUnusedFilter(filterGroups, filterConfiguration)
  const [searchValue, setSearchValue] = useState('')
  const dispatch = useAppDispatch()

  if (filterGroups.length === 1 && filterGroups[0].length === 0) {
    setFilterGroups(addFilter(defaultFilter, filterGroups))
  }

  if (filterGroups.length === 0) {
    setFilterGroups(addFilter(defaultFilter, Array(1).fill([])))
  }

  const handleAddFilterGroup = () => {
    segmentTrack(TrackType.FILTER_GROUP_ADDED)
    fsTrackEvent(FULLSTORY_CUSTOM_EVENT.FILTER_GROUP_ADDED)
    const newGroups = addFilterGroup(defaultFilter, filterGroups)
    setFilterGroups(newGroups)
    onChangeFilterGroups(newGroups)
  }

  const handleAddNewFilter = (
    firstUnusedFilter: FilterConfigAndAvailableValues,
    groupIndex: number
  ) => {
    const filterConfig = getFilterConfig(firstUnusedFilter, filterConfiguration)
    if (filterConfig) {
      const filter = formatFilterFromConfig(filterConfig)
      const newGroups = addFilter(filter, filterGroups, groupIndex)

      setFilterGroups(newGroups)
      onChangeFilterGroups(newGroups)

      segmentTrack(TrackType.FILTER_ADDED)
      fsTrackEvent(FULLSTORY_CUSTOM_EVENT.FILTER_ADDED)
    }
  }

  const handleChangeFilterType = (
    newValue: Record<string, any>,
    filterIndex: number,
    groupIndex: number
  ) => {
    if (newValue && newValue.value) {
      const nextFilterConfig = getFilterConfig(newValue.value, filterConfiguration)
      if (nextFilterConfig) {
        const nextFilter = formatFilterFromConfig(nextFilterConfig)
        const newGroups = updateFilterType(nextFilter, filterGroups, groupIndex, filterIndex)
        setFilterGroups(newGroups)
        onChangeFilterGroups(newGroups)
      }
    }
  }

  const handleChangeFilterOperator = (
    newValue: Record<string, any>,
    filterIndex: number,
    groupIndex: number
  ) => {
    if (newValue) {
      const newGroups = updateFilterOperator(newValue.value, filterGroups, groupIndex, filterIndex)
      setFilterGroups(newGroups)
      onChangeFilterGroups(newGroups)
    }
  }

  const handleChangeFilterValue = (
    value: Record<string, any>,
    filterIndex: number,
    groupIndex: number
  ) => {
    const newGroups = updateFilterValue(value, filterGroups, groupIndex, filterIndex)
    setFilterGroups(newGroups)
    onChangeFilterGroups(newGroups)
  }

  const handleRemoveFilter = (filterIndex: number, groupIndex: number) => {
    const isLastFilter = filterIndex === 0 && groupIndex === 0 && flatten(filterGroups).length === 1
    const newGroups = removeFilter(filterGroups, groupIndex, filterIndex)
    setFilterGroups(newGroups)
    onChangeFilterGroups(newGroups)

    if (isLastFilter) {
      dispatch(setDateRange(getDefaultRange()))
      dispatch(clearFilters({}))
      onClose && onClose()
    }
  }

  useEffect(() => {
    // Reset filter card filters when changing the active filter
    if (activeFilterId && activeFilterId !== INACTIVE_FILTER) {
      setFilterGroups(initialGroups)
    }
  }, [activeFilterId, initialGroups])

  return (
    <div>
      {filterGroups.map((filters: FilterValues[], groupIndex: number) => (
        <React.Fragment key={`${groupIndex}-${filters[0]?.filterType}`}>
          <FilterGroupedRow
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            filters={filters}
            groupIndex={groupIndex}
            showFormValidation={showFormValidation}
            filterConfiguration={filterConfiguration}
            calendarOpenDirection={calendarOpenDirection}
            handleChangeFilterType={handleChangeFilterType}
            handleChangeFilterOperator={handleChangeFilterOperator}
            handleChangeFilterValue={handleChangeFilterValue}
            handleRemoveFilter={handleRemoveFilter}
          />
          <div className={styles.addActionRow}>
            {firstUnusedFilter && (
              <ActionItem
                text="Add filter"
                icon="plus"
                trackingId="ProspectorFilterAction"
                onClick={() => handleAddNewFilter(firstUnusedFilter, groupIndex)}
              />
            )}
          </div>
          {groupIndex < filterGroups.length - 1 && (
            <div className={styles.orSeparator}>
              <span className={styles.orLine} />
              <span className={cx(styles.orText, utilityStyles.semiBoldWeight)}>or</span>
              <span className={styles.orLine} />
            </div>
          )}
        </React.Fragment>
      ))}
      <div className={styles.actionsContainer}>
        {filterGroups.length < MAX_NUM_FILTER_GROUPS && (
          <ActionItem
            text="Add group"
            icon="plus"
            trackingId="ProspectorFilterAddGroupAction"
            onClick={handleAddFilterGroup}
          />
        )}
      </div>
    </div>
  )
}

export default FiltersForm
